import { t, fmt } from '@/i18n';

/**
 * Minimal shape needed to render capacity + confirmation status. A subset of the
 * `CalendarEventSerializer` payload, so both `CalendarEvent` and the upcoming-list
 * events satisfy it. Shared by the owner page's Upcoming list and the calendar
 * day-detail panel so the capacity model stays in one place (issue #398).
 */
export interface CapacityEvent {
  max_capacity?: number | null;
  min_capacity?: number | null;
  confirmed_count?: number;
  confirmation_status?: string;
}

export interface ConfirmationDisplay {
  label: string;
  /** Classes for a bordered status chip. */
  chip: string;
  /** Text color for an inline status label. */
  tone: string;
  /** Background color for a status dot. */
  dot: string;
}

const RED: Omit<ConfirmationDisplay, 'label'> = {
  chip: 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-300',
  tone: 'text-red-700 dark:text-red-300',
  dot: 'bg-red-500',
};
const TEAL: Omit<ConfirmationDisplay, 'label'> = {
  chip: 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-400/30 dark:bg-teal-500/10 dark:text-teal-300',
  tone: 'text-teal-700 dark:text-teal-300',
  dot: 'bg-teal-500',
};
const AMBER: Omit<ConfirmationDisplay, 'label'> = {
  chip: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-300',
  tone: 'text-amber-700 dark:text-amber-300',
  dot: 'bg-amber-500',
};
const GRAY: Omit<ConfirmationDisplay, 'label'> = {
  chip: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400',
  tone: 'text-gray-500 dark:text-gray-400',
  dot: 'bg-gray-400',
};

/**
 * Human-readable confirmation status derived from the real backend fields
 * (`confirmation_status`, `confirmed_count`, `min_capacity`, `max_capacity`).
 * Mirrors the model semantics: full → waitlist, pending → "needs N more",
 * confirmed → running.
 */
export function confirmationDisplay(ev: CapacityEvent): ConfirmationDisplay {
  const max = ev.max_capacity ?? null;
  const min = ev.min_capacity ?? null;
  const confirmed = ev.confirmed_count ?? 0;

  if (max != null && max > 0 && confirmed >= max) {
    return { label: t('Full · waitlist'), ...RED };
  }

  switch (ev.confirmation_status) {
    case 'pending_confirmation': {
      const needed = min != null ? Math.max(0, min - confirmed) : 0;
      return { label: fmt('Needs %(count)s more to confirm', { count: needed }), ...AMBER };
    }
    case 'cancelled_min_not_met':
      return { label: t('Minimum not met'), ...GRAY };
    case 'confirmed':
    default:
      return min != null
        ? { label: t('Confirmed · running'), ...TEAL }
        : { label: t('Open for registration'), ...TEAL };
  }
}

function fillColor(pct: number): string {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 85) return 'bg-amber-500';
  return 'bg-teal-500';
}

function countColor(pct: number): string {
  if (pct >= 100) return 'text-red-600 dark:text-red-400';
  if (pct >= 85) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-500 dark:text-gray-400';
}

/**
 * Capacity bar showing `confirmed/max` with a `min_capacity` threshold tick.
 * Teal → amber (≥85%) → red (full). Used in the Upcoming list cards and the
 * calendar day-detail rows.
 */
export function CapacityBar({ event, size = 'sm' }: { event: CapacityEvent; size?: 'sm' | 'lg' }) {
  const max = event.max_capacity ?? 0;
  const min = event.min_capacity ?? null;
  const confirmed = event.confirmed_count ?? 0;
  const pct = max > 0 ? Math.round((confirmed / max) * 100) : 0;
  const minPos = max > 0 && min != null ? Math.min(100, Math.round((min / max) * 100)) : null;
  const big = size === 'lg';

  if (max <= 0) return null;

  return (
    <div className="flex items-center gap-2">
      <div className={`relative ${big ? 'h-2' : 'h-1.5'} flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800`}>
        <div
          className={`h-full rounded-full transition-[width] duration-700 ${fillColor(pct)}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
        {minPos != null && (
          <span
            className={`absolute top-1/2 w-0.5 -translate-y-1/2 ${big ? 'h-3' : 'h-2.5'} bg-gray-500/70 dark:bg-gray-400/70`}
            style={{ left: `${minPos}%` }}
            title={fmt('min %(count)s to confirm', { count: min ?? 0 })}
          />
        )}
      </div>
      <span className={`shrink-0 text-[11px] font-medium ${countColor(pct)}`}>
        {confirmed}/{max}
      </span>
    </div>
  );
}
